import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { CompanyRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { CompanyForm, type CompanyFormValues } from "@/modules/companies/components/CompanyForm";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { companyMutations } from "@/queries/companies/mutations";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { routes } from "@/routes";

export function EditCompanyPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const companyId = id ?? "";
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const companyQueries = useCompanyQueries();
  const companiesQuery = useQuery(companyQueries.list());
  const companyQuery = useQuery(companyQueries.details(companyId));
  const addressQueries = useAddressQueries();
  const addressesQuery = useQuery(addressQueries.list());
  const updateCompany = companyMutations.useEditCompany(companyId);
  const refreshLocationCode = companyMutations.useRefreshLocationCode();
  const error = companyQuery.error ?? companiesQuery.error ?? addressesQuery.error;

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (companyQuery.isFetching || companiesQuery.isPending || addressesQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const company = companyQuery.data;
  if (!company) {
    return <ErrorPage status={404} />;
  }

  const onSubmit = async (data: CompanyFormValues) => {
    const payload: CompanyRequest = {
      addressId: data.address.id,
      name: data.name,
      description: data.description,
      externalId: data.externalId,
    };
    const background = data.background.at(0);
    if (background) {
      payload.backgroundImageId = (await uploadFormImage(background))?.id;
    }
    const logo = data.logo.at(0);
    if (logo) {
      payload.logoId = (await uploadFormImage(logo))?.id;
    }
    await updateCompany.mutateAsync(payload);
    void navigate(routes.companies.list({ slug }));
  };

  return (
    <CompanyForm
      isEditMode
      defaultValues={{
        name: company.name,
        description: company.description,
        externalId: company.externalId,
        address: addressesQuery.data?.items.find((address) => address.id === company.address.id),
        background: company.backgroundImage ? [company.backgroundImage] : [],
        logo: company.logo ? [company.logo] : [],
      }}
      isSubmitting={isUploadingImage || updateCompany.isPending}
      locationCode={company.locationCode}
      isRefreshingLocationCode={refreshLocationCode.isPending}
      onRefreshLocationCode={() => refreshLocationCode.mutateAsync(companyId)}
      existingCompanies={companiesQuery.data?.items.filter((item) => item.id !== companyId) ?? []}
      addresses={addressesQuery.data?.items ?? []}
      onSubmit={onSubmit}
    />
  );
}
